﻿import { Injectable } from '@angular/core';
import { Utils } from '@shared/fork-common-controls/utils.service';

@Injectable()
export class FormValidationService {
  // TODO: this may become its own config
  // Master validation error structure, different keys will go here
  // based on the need for custom messages.
  // Also, different payloads will come in and may or may not be used at some point
  // depending on the validation type (i.e. required, pattern, maxlength, etc).
  public errors = {
    required: (controlId: string, payload?: any) => this.getRequiredErrorMessage(controlId),
    requiredCityStateZip: (controlId: string, payload?: any) => 'Enter a city, state, or zip.',
    homePaymentRequired: (controlId: string, payload) => this.getRequiredErrorMessage(controlId),
    pattern: (controlId: string, payload) => this.getPatternErrorMessage(controlId, payload),
    maxlength: (controlId: string, payload) => this.getMaxLengthErrorMessage(controlId, payload),
    minlength: (controlId: string, payload) => this.getMinLengthErrorMessage(controlId, payload),
    invalidState: (controlId: string, payload) => this.getInvalidStateMessage(),
    emailInUse: (controlId: string, payload) => this.getEmailInUseMessage(),
    maxCurrencyError: (controlId: string, payload) => `${controlId} ${payload}`,
    minCurrencyError: (controlId: string, payload) => `${controlId} ${payload}`,
    maxCurrencyShortError: (controlId: string, payload: any) => payload,
    minCurrencyShortError: (controlId: string, payload: any) => payload,
    minAge: (controlId: string, payload) => this.getMinAgeErrorMessage(payload),
    passwordRepeat: (controlId: string, payload?: any) => 'Password does not match.',
    loanAmountInvalid: (controlId: string, payload: any) => payload,
    maxValueError: (controlId: string, payload: any) => `${controlId} ${payload}`,
    minValueError: (controlId: string, payload: any) => `${controlId} ${payload}`,
    ceilingValueError: (controlId: string, payload: any) => `${controlId} ${payload}`,
    floorValueError: (controlId: string, payload: any) => `${controlId} ${payload}`,
    dateBeforeToday: (controlId: string, payload) => payload,
    dateAheadToday: (controlId: string, payload: any) => payload,
    mustBeValidDate: (controlId: string, payload: any) => `${controlId} ${payload}`,
    addressNumberMaxLimit: (controlId: string, payload: any) => payload,
    stateLicensing: (controlId: string, payload: any) => payload,
    genericError: (controlId: string, payload: any) => payload,
    poBoxNotAllowed: (controlId: string, payload?: any) => 'PO Box is not allowed. Enter the street address where you live.',
    badSsn: (controlId: string, payload: any) => payload,
    duplicateSsn: (controlId: string, payload: any) => payload,
    letterOrNumber: (controlId: string, payload: any) => 'Password must contain one letter or number (e.g. abc123)',
    specialCharacter: (controlId: string, payload: any) => 'Password must contain one symbol (e.g. @#$%)',
    nonNumericInput: (controlId: string, payload: any) => 'Input must be numeric',
    mustAgreeError: (controlId: string, payload: any) => payload
  };

  // Custom error messages for 'required'.
  public requiredDict = {
    dateStartedLivingHere: 'Date you started living here is required.',
    previousAddress: 'Previous address is required.',
    previousDateStartedLivingHere: 'Previous living date is required.',
    gender: 'Please select gender.',
    ethnicity: 'Please select ethnicity.',
    raceOrOrigin: 'Please select Race or National Origin.',
    employmentInfo: 'Please select an employment status.',
    occupancyStatus: 'Please select an occupancy status.',
    oldPassword: 'Please enter your current password',
    newPassword: 'Please enter your new password'
  };

  // Custom error messages for 'pattern'.
  public patternDict = {
    'password': 'Sorry we don’t support `. Please select a different special character.',
    'promoCode': 'Invalid invitation code, please try again.'
  };

  // Custom error messages for 'maxlength'.
  public maxLengthDict = {
  };

  // Custom error messages for 'minlength'.
  public minLengthDict = {
    workPhone: 'Work phone number needs to be 10 digits.',
    newPassword: 'Password must be at least 8 characters',
    confirmPassword: 'Password must be at least 8 characters'
  };

  // Please don't remove the '$^|^null$|^undefined$|' at the start of each regex.
  // If a field is required, please add the 'required' attribute
  public regex = {
    numDependents: // 0 to 13
      '^$|^null$|^undefined$|^[0-9]$|^1[0-3]$',
    // Don't make the email regex crazy, the backend needs to do the more robust validation
    // TODO: Error for @loandepot.com emails.
    email:
      '^$|^null$|^undefined$|^.+@.+',
    // Per Rita Stranger: The expectation is that we detect '@loandepot.com' and pass it. That's all you should do.
    // Given this, this promo code only cares that there's a '@loandepot.com' of either upper or lowercase
    // at the end of the entry.
    promoCode:
      /^$|^null$|^undefined$|^.+@loandepot\.com$/i,
    yearsInSchool: // less than or equal to 50 years
      '^$|^null$|^undefined$|^[0-9]$|^[1-4][0-9]$|^50$',
    phone: // empty string or (111) 123-4567
      '^$|^null$|^undefined$|^\\(\\d(?!1{2})\\d{2}\\) \\d{3}-\\d{4}$',
    phoneWithExt: // empty string or (111)123-4567 Ext. 12345
      '^$|^null$|^undefined$|^\\(\\d{3}\\)\\d{3}-\\d{4} Ext\\. ?(\\d{0,5})$',
    phoneExt:
    // currently only used in one page in Mortgage.
    // Empower only allows 13 total digits for phones (including extension).
    // but if PL uses a different validation rule, may need to extend this regex, or make separate regexes for
    // Mortgage and PL
      '^$|^null$|^undefined$|^x?\\d{3}$',
    ssn: // 000-00-0000
      '^$|^null$|^undefined$|\\d{3}-\\d{2}-\\d{4}',
    creditReportReferenceNumber: // letters, numbers, and dashes
      '^$|^null$|^undefined$|^([A-Za-z0-9\-]+)',
    date: // dd/mm/yyyy
      /^$|^null$|^undefined$|^\d{2}\/\d{2}\/(19|20)\d{2}$/,
    currency: // allows negative numbers
      '^$|^null$|^undefined$|^0$|^\\-?[0-9]+(?:\\.\\d{1,4})?$',
    zipCode: // 5 or 9-digit (requires hyphen)
      '^$|^null$|^undefined$|^\\d{5}$|^\\d{5}\\-\\d{4}$',
    positiveCurrency:
    '^$|^null$|^undefined$|^0$|' +
    // '$' followed (optionally) by 1-3 digits and a comma, (the '$12' in '$12,000'
    // followed by (0-n) sets of 3 digits and a comma, (the '000,' in '$55,000,000'
    // followed by exactly 3 digits (the 440 in '$12,440'
    // followed optionally by '.' and 1-4 digits (the .65 in $12.65)
    '^\\$(\\d{1,3},)?([0-9]{3},)*\\d{3}(?:\\.\\d{1,4})?$|' +
    // '$' followed by 1-3 digits, and the optional decimals. used for values < $1,000
    '^\\$\\d{1,3}(?:\\.\\d{1,4})?$',
    yearsOwnedRented:
      '^$|^null$|^undefined$|^0.\\d{1,3}$|^[1-9](.\\d{1,3})?$|^[1-9][0-9](.\\d{1,3})?$|^99$',
    numberOfUnits:
      '^$|^null$|^undefined$|^\\d',
    yearBuilt: // 1000 to 2099
      '^$|^null$|^undefined$|^[12][0-9]{3}$',
    yearsInProfession:
      '^$|^null$|^undefined$|^0.\\d{1,3}$|^[1-9](.\\d{1,3})?$|^[1-4][0-9](.\\d{1,3})?$|^50$',
    yearlyIncome: // up to 12 digits
      '^$|^null$|^undefined$|^0|^\\d{1,12}$',
    driversLicenseNumber:    {
      mortgage: '^$|^null$|^undefined$|^\\w{13}$'
    },
    employerName: // min length 1, max length 255
      '^$|^null$|^undefined$|(?=.{1,255})\\S',
    employmentPosition: // min length 1, max length 255
      '^$|^null$|^undefined$|(?=.{1,255})\\S',
    workAddress: // min length 1, max length 255
      '^$|^null$|^undefined$|^.{1,255}$',
    workCity: // min length 1, max length 255
      '^$|^null$|^undefined$|^.{1,255}$',
    workPhone: // min length 14 including parenthesis, dash and space
      '^$|^null$|^undefined$|^\\(\\d(?!1{2})\\d{2}\\) \\d{3}-\\d{4}$',
  };

  public placeholder = {
    phone:         '(000)999-8888',
    phoneWithExt:  '(000)999-8888 Ext. 000',
    ssn:           '000-00-0000',
    date:          'yyyy-mm-dd', // placeholder for unsupported browsers
  };

  public maxlength = {
    dependentAges:           24,
    email:                   80,
    yearsInSchool:           2,
    // valid format on front end: (###) ###-####
    phone:                   14,
    phoneWithExt:            24, // TODO
    phoneExt:                5,
    ssn:                     11,
    date:                    10,
    firstAndLastName:        {
      mortgage: 130,
      pl: 255
    },
    firstName:               {
      mortgage: 20,
      pl: 130
    },
    lastName:                {
      mortgage: 75,
      pl: 130
    },
    middleName:              20, // TODO
    currency:                13,  // TODO
    positiveCurrency:        {    // TODO
      // assumption: all positive currency fields are 14 digits max in mortgage
      // which means 19 characters total: $ddd,ddd,ddd,ddd.dd
      mortgage: 19,
      personalLoans: 12,

      // mortgage page with different max currency length
      // total 15 characters: $ddd,ddd,ddd.dd
      wherePlanningToBuy: 15
    },
    yearlyIncome:            {
      mortgage: 17,
      pl: 12
    },
    address:                 {
      mortgage: 49 // mortgage address is defined as street # (8) + street name (40),
                   // and since this max is for both units, add an extra for the deliminator character
    },
    workAddress: {
      mortgage: 55
    },
    addressUnit: {
      mortgage: 8
    },
    state:                   2,
    zipCode:                 10,  // 10 with hyphen (i.e. 22222-3333)
    city:                    {
      mortgage: 50,
      pl: 255
    },
    county:                  255, // TODO
    yearsOwnedRented:        5,
    unitNumber:              8,
    numberOfUnits:           15,
    yearBuilt:               4,
    yearsInProfession:       6, // allow for decimals like 13.255 (empower allows 3)
    housingExpenseOtherName: 20,
    explanation:             255,
    employerName:            {
      mortgage: 40
    },
    employeePosition:        {
      mortgage: 30
    },
    militaryPayRank: {
      mortgage: 30
    },
    accountName: 40
  };

  public min = {
    cashout: 501.0
  };

  public max = {
    currency: {
      personalLoans: 999999999.99,
      // assumption: all positive currency fields are 14 digits max in mortgage,
      // 2 of which are for the cents
      mortgage: 999999999999.99,
    },
    yearsResidency: 400,
    monthsResidency: 11,
    wherePlanningToBuy: 999999999.99,
    yearlyIncome: {
      pl: 999999999,
      mortgage: 9999999999.99
    },
    estimatedPropertyValue: 9999999.99,
    estimatedPropertyTax: {
      mortgage: 9999999.99
    },
    estimatedHOA: 9999999.99,
    estimatedHomeOwnersInsurance: {
      mortgage: 9999999.99
    },
    monthlyMortgagePayment: 9999999.99,
    remainingMortgageBalance: 999999999.99,
    monthlyRentalPayment: 9999999.99,
    yearsWithEmployer: {
      mortgage: 99
    },
    employedAnnualSalary: {
      mortgage: 9999999.99
    },
    militaryPayAnnualSalary: {
      mortgage: 9999999.99
    },
    pensionMonthlyIncome: {
      mortgage: 9999999.99
    },
    socialSecurityMonthlyIncome: {
      mortgage: 9999999.99
    },
    otherMonthlyIncome: {
      mortgage: 9999999.99
    },
    numDependents: {
      mortgage: 30
    },
    dependentsAge: {
      mortgage: 99
    },
    monthlyExpensesMortgage: 999999999.99,
    monthlyExpensesPropertyTax: 99999.99,
    monthlyExpensesHazardInsurance: 99999.99,
    monthlyExpensesHomeowners: 99999.99,
    monthlyExpensesMortgageInsurance: 99999.99,
    accountBalance: 999999999.99
  };

  private getEmailInUseMessage() {
    return 'Email is registered. Please sign-in.';
  }

  private getInvalidStateMessage(): string {
    return 'Loans unavailable in WV.';
  }

  // Get a custom error message for 'required' based on controlId.
  // Return a default error message for 'required' if none found.
  private getRequiredErrorMessage(controlId: string): string {
    let errorMessage = this.requiredDict[controlId];
    if (!errorMessage) {
      return 'Please enter a ' + controlId + '.';
    }
    return errorMessage;
  }

  private getPatternErrorMessage(controlId: string, payload): string {
    let errorMessage = this.patternDict[controlId];
    if (!errorMessage) {
      return 'Please enter a valid ' + controlId + '.';
    }
    return errorMessage;
  }

  private getMaxLengthErrorMessage(controlId: string, payload): string {
    let errorMessage = this.maxLengthDict[controlId];
    if (!errorMessage) {
      return controlId + ` cannot exceed ${payload.requiredLength} characters.`;
    }
    return errorMessage;
  }

  private getMinLengthErrorMessage(controlId: string, payload): string {
    let errorMessage = this.minLengthDict[controlId];
    if (!errorMessage) {
      return controlId + ` cannot be less than ${payload.requiredLength} characters.`;
    }
    return errorMessage;
  }

  private getMinAgeErrorMessage(payload): string {
   return payload ? payload : 'You must be 18 or older to apply for a loan.';
  }
}
